import { fetchConfig, sendHeartbeat } from './api/config';
import { configCache } from './cache/memory';
import { parseConfig } from './config/parse';
import { emitDebug, setDebugEnabled } from './debug';
import { clearInjectedScripts, injectEligibleScripts } from './scripts/inject';
import { bindRuntime, mountConsentUi } from './sdk';
import { applyTheme } from './theme';
import type { ConsentState, RuntimeConfig } from './types';
import { getPreviewToken, getSiteKey, isDebugRequested, ready } from './utils/dom';

async function apply(config: RuntimeConfig, consent: ConsentState | null): Promise<void> {
  // Always inject essential-safe scripts; gated scripts need matching consent (AND).
  injectEligibleScripts(config.scripts, config.environment, consent, config.consent.categories);
}

async function bootstrap(): Promise<void> {
  const previewToken = getPreviewToken();
  const siteKey = getSiteKey() || (previewToken ? 'preview' : null);

  if (!siteKey) {
    console.warn('[Orbit] Missing data-site attribute on runtime script.');
    return;
  }

  setDebugEnabled(isDebugRequested());

  const load = async () => {
    clearInjectedScripts();
    const raw = await fetchConfig(siteKey);
    const config = parseConfig(raw);
    configCache.set(config);
    applyTheme(config.consent.theme);
    emitDebug('config_loaded', config.preview ? 'Draft preview configuration loaded' : 'Configuration loaded', {
      version: config.version,
      preview: Boolean(config.preview),
      permissions: config.consent.categories.map((c) => c.id),
      integrations: config.scripts.filter((s) => s.enabled).length,
    });
    const consent = mountConsentUi(config, siteKey);
    await apply(config, consent);
  };

  const sdk = bindRuntime({ siteKey, load, apply });
  window.Orbit = sdk;

  try {
    await load();
    // Send a heartbeat once per session so the portal can detect the snippet.
    const beaconKey = `orbit_beacon_${siteKey}`;
    if (!sessionStorage.getItem(beaconKey)) {
      sendHeartbeat(siteKey);
      try { sessionStorage.setItem(beaconKey, '1'); } catch { /* private browsing */ }
    }
  } catch (error) {
    console.warn('[Orbit] Failed to bootstrap', error);
  }
}

ready(() => {
  void bootstrap();
});
